import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { SearchSubscriptionUserData } from '@immosky/types';
import { PrivacyStateService } from '../../services/privacy-state';
import { SearchSubscriptionService } from '../../services/search-subscription.service';

type OnChangeCallback = (userData: SearchSubscriptionUserData) => void;
type OnTouchedCallback = () => void;

@Component({
  selector: 'lib-search-subscription-form',
  templateUrl: './search-subscription-form.component.html',
  styleUrls: ['./search-subscription-form.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SearchSubscriptionFormComponent,
      multi: true,
    },
  ],
})
export class SearchSubscriptionFormComponent
  implements ControlValueAccessor, AfterViewInit
{
  private onChangeCallback!: OnChangeCallback;
  private onTouchedCallback!: OnTouchedCallback;

  canSubmitForm = false;

  firstNameControl = new FormControl('', [Validators.required]);
  lastNameControl = new FormControl('', [Validators.required]);
  emailControl = new FormControl('', [Validators.required, Validators.email]);
  subscribeToNewsletterControl = new FormControl(false, [Validators.required]);
  termsControl = new FormControl(false, [Validators.requiredTrue]);

  form!: FormGroup;

  get isFirstNameInvalid() {
    return this.firstNameControl.invalid && this.firstNameControl.touched;
  }

  get isLastNameInvalid() {
    return this.lastNameControl.invalid && this.lastNameControl.touched;
  }

  get isEmailInvalid() {
    return this.emailControl.invalid && this.emailControl.touched;
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private searchSubscriptionService: SearchSubscriptionService,
    private elementRef: ElementRef,
    private privacyStateService: PrivacyStateService
  ) {
    this.form = this.formBuilder.group({
      firstName: this.firstNameControl,
      lastName: this.lastNameControl,
      email: this.emailControl,
      subscribeToNewsletter: this.subscribeToNewsletterControl,
      terms: this.termsControl,
    });

    this.form.valueChanges.subscribe((userData: SearchSubscriptionUserData) => {
      this.onChangeCallback?.(userData);
      this.searchSubscriptionService.setUserData(userData);
      this.searchSubscriptionService.setIsUserDataValid(this.form.valid);
    });
  }

  ngAfterViewInit() {
    this.onLoaded();
    this.watchPrivacyState();
  }

  private onLoaded = () => {
    if (document.readyState === 'complete') {
      // FIXME: at some point we should make WebComponents for things like .mdc-text-field
      //   instead of manually calling .registerChildren()
      // @ts-ignore
      window.componentRegistry.registerChildren(this.elementRef.nativeElement);
    } else {
      window.requestAnimationFrame(this.onLoaded);
    }
  };

  private watchPrivacyState() {
    this.privacyStateService.canSubmitForm.subscribe(
      (canSubmitForm: boolean) => {
        this.canSubmitForm = canSubmitForm;

        if (this.canSubmitForm) {
          this.form.enable();
        } else {
          this.form.disable();
        }

        this.cdRef.detectChanges();
      }
    );
  }

  registerOnChange(fn: OnChangeCallback): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.disable();
    } else {
      this.enable();
    }
  }

  disable() {
    this.form.disable();
  }

  enable() {
    this.form.enable();
  }

  writeValue(userData: SearchSubscriptionUserData): void {
    this.form.setValue(
      {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        subscribeToNewsletter: userData.subscribeToNewsletter,
      },
      {
        emitEvent: false,
      }
    );
  }

  openCookieSettings() {
    console.log('open cookie settings');
    this.privacyStateService.openCookieSettings();
  }
}
